import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { BookOpen, DoorOpen, KeyRound, Loader2, LogOut, MapPin, Phone, ShieldAlert, Sparkles, Wifi } from "lucide-react";
import api from "../lib/api";

const T = {
  fr: { kicker: "Livret d'accueil", arrivee: "Arrivée", equipements: "Équipements", regles: "Règles de la maison", depart: "Départ", recommandations: "Nos bonnes adresses", urgences: "Urgences", net: "Réseau", pwd: "Mot de passe", call: "Appeler votre hôte" },
  en: { kicker: "Welcome guide", arrivee: "Check-in", equipements: "Amenities", regles: "House rules", depart: "Check-out", recommandations: "Local tips", urgences: "Emergencies", net: "Network", pwd: "Password", call: "Call your host" },
};
const SECTIONS = [["arrivee", KeyRound], ["equipements", Sparkles], ["regles", BookOpen], ["depart", LogOut], ["recommandations", MapPin], ["urgences", ShieldAlert]];
const initialLang = (q) => (q === "fr" || q === "en" ? q : (navigator.language || "fr").toLowerCase().startsWith("fr") ? "fr" : "en");

function LangSwitch({ lang, setLang }) {
  return (
    <div className="inline-flex p-1 rounded-full bg-white/10 border border-white/20" data-testid="livret-public-lang">
      {["fr", "en"].map((k) => <button key={k} onClick={() => setLang(k)} className={`px-3 py-1 rounded-full text-xs font-bold uppercase transition-colors ${lang === k ? "bg-brand-lime text-brand-greenDeep" : "text-white hover:bg-white/10"}`} data-testid={`livret-public-lang-${k}`}>{k}</button>)}
    </div>
  );
}

export default function LivretAccueil() {
  const { token } = useParams();
  const [sp] = useSearchParams();
  const [raw, setRaw] = useState(null);
  const [error, setError] = useState("");
  const [lang, setLang] = useState(() => initialLang(sp.get("lang")));
  useEffect(() => { api.get(`/public/livret/${token}`).then((r) => setRaw(r.data)).catch(() => setError("Ce livret n'est pas disponible. / This guide is not available.")); }, [token]);
  if (error) return <div className="min-h-screen flex items-center justify-center p-6 text-brand-greenDeep" data-testid="livret-public-error">{error}</div>;
  if (!raw) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  const eff = lang === "en" && raw.has_en ? "en" : "fr";
  const lv = eff === "en" ? { ...raw, ...raw.en } : raw;
  const t = T[eff];
  const p = raw.property || {};
  return (
    <div className="min-h-screen bg-brand-softer pb-14" data-testid="livret-public" lang={eff}>
      <header className="bg-brand-greenDeep text-white px-6 pt-8 pb-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-brand-lime font-semibold uppercase tracking-[0.2em] flex items-center gap-2"><DoorOpen className="w-3.5 h-3.5" /> {t.kicker}</p>
            {raw.has_en && <LangSwitch lang={lang} setLang={setLang} />}
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-semibold mt-4" data-testid="livret-public-title">{lv.titre || p.name}</h1>
          <p className="text-sm text-white/60 mt-2">{[p.address, p.postal_code, p.city].filter(Boolean).join(" ")}</p>
        </div>
      </header>
      <main className="max-w-2xl mx-auto px-5 -mt-4 space-y-4">
        {lv.bienvenue && <section className="bg-white rounded-2xl p-6 border border-brand-soft text-brand-greenDeep leading-relaxed whitespace-pre-line" data-testid="livret-public-bienvenue">{lv.bienvenue}</section>}
        {(lv.wifi_nom || lv.wifi_mdp) && (
          <section className="bg-brand-lime/30 rounded-2xl p-5 border border-brand-lime flex items-center gap-4" data-testid="livret-public-wifi">
            <Wifi className="w-6 h-6 text-brand-greenDeep shrink-0" />
            <div className="text-sm text-brand-greenDeep"><p>{t.net} : <b>{lv.wifi_nom}</b></p><p>{t.pwd} : <b className="select-all">{lv.wifi_mdp}</b></p></div>
          </section>
        )}
        {SECTIONS.filter(([k]) => lv[k]).map(([k, Icon]) => (
          <section key={k} className="bg-white rounded-2xl p-6 border border-brand-soft" data-testid={`livret-public-${k}`}>
            <h2 className="flex items-center gap-2 font-semibold text-brand-greenDeep mb-2"><Icon className="w-4 h-4 text-brand-limeDark" /> {T[eff][k]}</h2>
            <p className="text-sm text-brand-green/75 whitespace-pre-line leading-relaxed">{lv[k]}</p>
          </section>
        ))}
        {lv.contact_tel && <a href={`tel:${lv.contact_tel}`} className="flex items-center justify-center gap-2 w-full py-3.5 rounded-full bg-brand-greenDeep text-white font-semibold" data-testid="livret-public-call"><Phone className="w-4 h-4" /> {T[eff].call}</a>}
        {raw.booking_token && <a href={`/reserver/${raw.booking_token}`} className="block rounded-2xl bg-brand-lime p-5 text-center text-brand-greenDeep hover:bg-brand-limeDark transition-colors" data-testid="livret-public-book">
          <p className="font-display text-xl font-semibold">{eff === "en" ? "Book your next stay with no fees" : "Réserver votre prochain séjour sans frais"}</p>
          <p className="text-xs mt-1 opacity-70">{eff === "en" ? "Directly with your host, no platform commission" : "Directement auprès de votre hôte, sans commission de plateforme"}</p>
        </a>}
        <p className="text-center text-xs text-brand-green/40 pt-4">Propulsé par 360immoinvest</p>
      </main>
    </div>
  );
}
